import axios from 'axios'
import { refreshAccessToken } from '@/apiManager/auth'
import i18n from '@/libs/i18n'
import '@/apiManager/deviceIdHeader'

const axiosApiInstance = axios.create()

const getBaseUrl = baseUrl => ({
  base: process.env.VUE_APP_BASE_URL,
  gamification: process.env.VUE_APP_GAMIFICATION,
  media: process.env.VUE_APP_MEDIA_SERVICE,
  user: process.env.VUE_APP_USER_SERVICE,
  userAuth: process.env.VUE_APP_USER_AUTH_SERVICE,
  common: process.env.VUE_APP_COMMON_SERVICE,
  reset: process.env.VUE_APP_RESET_SERVICE,
  instructor: process.env.VUE_APP_INSTRUCTORS,
  zone: process.env.VUE_APP_ZONE,
  attendance: process.env.VUE_APP_ATTENDANCE,
  challenge: process.env.VUE_APP_CHALLENGE,
}[baseUrl])
const getToken = () => localStorage.getItem('token')

const getClientTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'Asia/Dubai'
  }
}

const getAuthHeader = service => {
  const token = getToken()
  // Server-owned copy (activity type names, error messages) can only be
  // localized by the backend. Advertising the active locale is the standard way
  // to ask for it; harmless if the backend ignores it.
  const contextHeaders = {
    'X-Client-Timezone': getClientTimezone(),
    'Accept-Language': i18n.locale,
  }
  if (service === 'other') {
    return token
      ? {
        auth: `Bearer ${token}`,
        ...contextHeaders,
      }
      : contextHeaders
  }
  return token
    ? {
      Authorization: `Bearer ${token}`,
      ...contextHeaders,
    }
    : contextHeaders
}

const getConfigurations = (configurations, service) => {
  const { headers: customHeaders, ...rest } = configurations
  return {
    headers: {
      ...getAuthHeader(service),
      ...customHeaders,
    },
    ...rest,
  }
}
const getParams = (...params) => params.filter(param => !!param)

const api = (
  type,
  url,
  data = null,
  service = 'other',
  configurations = {},
) => {
  const params = getParams(
    getBaseUrl(service) + url,
    data,
    getConfigurations(configurations, service),
  )
  return axios[type](...params)
    .then(response => response.data)
    .catch(async error => {
      const originalRequest = error.config
      if (error.response.status === 401 || error.response.status === 403) {
        // A 401/403 from the refresh call ITSELF means the session is over —
        // the account was disabled or deleted mid-session. Refreshing again
        // lands right back here, so end the session instead of looping. The
        // server answers this case with 400 (handled below), but a 401 must
        // not be able to spin forever if that ever changes.
        if (url?.includes('refresh-token')) {
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('token')
          localStorage.removeItem('userData')
          window.location.href = '/'
          return error.response.data
        }
        originalRequest._retry = true
        const accessToken = await refreshAccessToken()
        localStorage.setItem('token', `${accessToken.data.jwtToken}`)
        originalRequest.headers.Authorization = `Bearer ${accessToken.data.jwtToken}`
        return axiosApiInstance(originalRequest)
      }
      if (error.response.status === 400 && url.includes('refresh-token')) {
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('token')
        localStorage.removeItem('userData')
        window.location.href = '/'
      } else {
        return error.response.data
      }
    })
}

export default api
